import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "./HelpPanel.css";

const HELP_CONTENT = {
  "/procurement/rfq": {
    title: "RFQ — Request for Quotation",
    phase: "Procurement · Phase 1",
    steps: [
      { n: 1, action: 'Click "+ New RFQ"', detail: "Opens the RFQ creation form." },
      { n: 2, action: "Fill header + line items → Save Draft", detail: "Branch, Date, Required By, Subject, Category. Add items with qty, unit, est. rate." },
      { n: 3, action: "Submit for Approval", detail: "RFQ enters workflow. Status → PENDING APPROVAL.", badge: "amber" },
      { n: 4, action: "Approver approves in My Inbox", detail: "Status → APPROVED.", badge: "green" },
      { n: 5, action: "Send to Suppliers", detail: "Select vendors → each receives a portal link. Status → SENT.", badge: "blue" },
      { n: 6, action: "Vendor submits quote via portal link", detail: "No WTI login needed. Vendor fills rates per line and submits." },
      { n: 7, action: "View Comparison → Accept Quote", detail: "Best price highlighted. Accept winner → RFQ becomes FINALIZED, Quotation auto-created.", badge: "green" },
    ],
  },
  "/procurement/rfq-create": {
    title: "RFQ Form",
    phase: "Procurement · Phase 1",
    steps: [
      { n: 1, action: "Fill Branch, RFQ Date, Required By, Subject, Category", detail: "All fields required before you can submit." },
      { n: 2, action: "Add line items", detail: "Each line needs: Item, Qty, Unit, Estimated Rate." },
      { n: 3, action: "Tick Quotation Requirements (optional)", detail: "Checkboxes for delivery, warranty, sample, etc." },
      { n: 4, action: "Save Draft", detail: "Saves without submitting. You can return and edit." },
      { n: 5, action: "Submit for Approval when ready", detail: "RFQ becomes read-only once submitted.", badge: "amber" },
    ],
  },
  "/procurement/rfq-comparison": {
    title: "RFQ Comparison",
    phase: "Procurement · Phase 1",
    steps: [
      { n: 1, action: "Review vendor quotes side by side", detail: "Lowest rate per line item is highlighted. Overall best-priced vendor is shown." },
      { n: 2, action: "Click 'Accept Quote' on the winning vendor", detail: "RFQ → FINALIZED. Quotation is auto-created from the winning vendor's rates.", badge: "green" },
      { n: 3, action: "Go to Quotations list", detail: "Find the newly created quotation and submit it for approval." },
    ],
  },
  "/procurement/quotations": {
    title: "Quotations",
    phase: "Procurement · Phase 2",
    steps: [
      { n: 1, action: "Find the DRAFT quotation → View it", detail: "Auto-created quotations come from an accepted RFQ." },
      { n: 2, action: "Submit for Approval", detail: "Status → IN APPROVAL.", badge: "amber" },
      { n: 3, action: "Approver approves in My Inbox", detail: "Status → APPROVED.", badge: "green" },
      { n: 4, action: "Click 'Create Purchase Order'", detail: "PO Create form opens, pre-filled from the quotation." },
    ],
  },
  "/procurement/quotation-view": {
    title: "Quotation View",
    phase: "Procurement · Phase 2",
    steps: [
      { n: 1, action: "Review vendor, payment terms, line items", detail: "Payment terms: ADVANCE_100 / ADVANCE_50_GRN_50 / GRN_100." },
      { n: 2, action: "Submit for Approval (if DRAFT)", detail: "Enters the approval workflow.", badge: "amber" },
      { n: 3, action: "Create PO (if APPROVED)", detail: "Navigates to PO Create with this quotation's data.", badge: "green" },
    ],
  },
  "/procurement/purchase-orders": {
    title: "Purchase Orders",
    phase: "Procurement · Phase 3",
    steps: [
      { n: 1, action: "Click '+ New PO' or come from Quotation → Create PO", detail: "" },
      { n: 2, action: "Review line items, payment terms, delivery date → Save Draft", detail: "Status → DRAFT.", badge: "grey" },
      { n: 3, action: "Submit for Approval", detail: "Status → IN APPROVAL.", badge: "amber" },
      { n: 4, action: "Approver approves in My Inbox", detail: "Status → APPROVED.", badge: "green" },
      { n: 5, action: "Go to PO View to enter GRN", detail: "" },
    ],
  },
  "/procurement/po-view": {
    title: "PO View",
    phase: "Procurement · Phase 3–6",
    steps: [
      { n: 1, action: "GRN tab → Enter GRN (when APPROVED)", detail: "Record received goods: delivery date, challan no., accepted/rejected qty per line.", badge: "blue" },
      { n: 2, action: "Returns tab → Create VRN (if items rejected)", detail: "VRN flow: DRAFT → DISPATCHED → ACKNOWLEDGED → Debit Note → Issue DN." },
      { n: 3, action: "Invoice tab → Verify Invoice", detail: "Enter Invoice No., Date, GST %, amounts. Attach vendor PDF. System runs 3-way match.", badge: "blue" },
      { n: 4, action: "Payments tab → Request Payment", detail: "Select stage (ADVANCE / GRN_BALANCE / MANUAL), enter amount → Submit.", badge: "amber" },
      { n: 5, action: "Payments tab → Disburse (after approval)", detail: "Mode: Cash/Cheque/UPI/NEFT. Enter date, amount, reference, upload proof.", badge: "green" },
    ],
  },
  "/procurement/grn-entry": {
    title: "GRN Entry",
    phase: "Procurement · Phase 4",
    steps: [
      { n: 1, action: "Fill Delivery Date and Delivery Challan No.", detail: "Both required." },
      { n: 2, action: "Per line: enter Received, Accepted, Rejected qty", detail: "Received = Accepted + Rejected (auto-validated)." },
      { n: 3, action: "If any qty rejected: set Rejection Type + Reason", detail: "Types: DAMAGED / WRONG_ITEM / SHORT_EXPIRY / QUANTITY_SHORTAGE / QUALITY_FAILURE." },
      { n: 4, action: "Click 'Record GRN'", detail: "GRN is saved. Stock IN posted to Inventory. You are redirected to My Inbox.", badge: "green" },
    ],
  },
  "/procurement/invoice-verify": {
    title: "Invoice Verification",
    phase: "Procurement · Phase 5",
    steps: [
      { n: 1, action: "Enter Invoice No. and Invoice Date (required)", detail: "" },
      { n: 2, action: "Enter Vendor GSTIN, GST Rate %, Taxable Amount, Invoice Total", detail: "GST and total amounts auto-calculate." },
      { n: 3, action: "Review per-line qty and rate", detail: "Variance cells show in red. Overall match badge: MATCHED or VARIANCE." },
      { n: 4, action: "Attach vendor's invoice PDF/image", detail: "Required before saving." },
      { n: 5, action: "Click 'Save & Verify'", detail: "3-way match result is stored. Returns to PO View.", badge: "green" },
    ],
  },
  "/job-card": {
    title: "Job Cards (R&M)",
    phase: "Repair & Maintenance",
    steps: [
      { n: 1, action: "Click '+ New Job Card'", detail: "" },
      { n: 2, action: "Select Vehicle, Service Center, Job Type, Issue Type → describe issue → Save", detail: "Status → OPEN.", badge: "grey" },
      { n: 3, action: "Add Estimate: parts + labour → Submit Estimate for Approval", detail: "Status → ESTIMATE PENDING.", badge: "amber" },
      { n: 4, action: "Approver approves estimate in My Inbox", detail: "Status → ESTIMATE APPROVED.", badge: "green" },
      { n: 5, action: "Work begins at service center", detail: "Parts consumed from Inventory against this JC. Status → IN PROGRESS.", badge: "blue" },
      { n: 6, action: "Close Job Card — enter actual parts + actual cost", detail: "Status → CLOSE PENDING.", badge: "amber" },
      { n: 7, action: "Approver approves closure in My Inbox", detail: "Status → CLOSED.", badge: "green" },
    ],
  },
  "/inventory": {
    title: "Inventory & Stock",
    phase: "Inventory",
    steps: [
      { n: 1, action: "Stock Levels tab — stock per branch for every PART / CONSUMABLE in Fleet Item Master", detail: "Select a branch to set min stock, reorder level and bin location. Items are created in Fleet Item Master." },
      { n: 2, action: "Stock IN comes from Procurement GRN", detail: "Accepted qty on a PO GRN is added to that branch's stock automatically.", badge: "green" },
      { n: 3, action: "Stock Issue tab — issue against a Requisition", detail: "Requisitions are raised manually or from a JC Estimate ('Request Parts'). Cannot issue more than branch stock.", badge: "blue" },
      { n: 4, action: "Stock Adjustment tab — opening stock, count corrections, damage / write-off", detail: "Reason is mandatory. Adjustments cannot be edited — post a reverse adjustment to correct.", badge: "amber" },
      { n: 5, action: "Stock Ledger tab — every IN / OUT movement with document no. and running balance", detail: "Filter by branch, item and date." },
    ],
  },
  "/my-inbox": {
    title: "My Inbox",
    phase: "Approvals",
    steps: [
      { n: 1, action: "Tasks assigned to you appear here automatically", detail: "Workflow engine assigns tasks based on your role configuration in Workflow Builder." },
      { n: 2, action: "Click a task to open it", detail: "A detail panel slides in showing the full request (RFQ, Quotation, PO, JC estimate, etc.)." },
      { n: 3, action: "Review → Approve or Reject", detail: "Add remarks if required (mandatory/optional depends on workflow config)." },
      { n: 4, action: "On final-stage approval — the source record status updates automatically", detail: "E.g. PO moves to APPROVED, Driver Left request is processed." },
    ],
  },
};

function getContent(pathname) {
  if (HELP_CONTENT[pathname]) return HELP_CONTENT[pathname];
  // partial match — e.g. /procurement/po-create → /procurement/purchase-orders
  const fallbacks = {
    "/procurement/po-create":        "/procurement/purchase-orders",
    "/procurement/quotation-create": "/procurement/quotations",
    "/procurement/grn":              "/procurement/po-view",
  };
  return HELP_CONTENT[fallbacks[pathname]] || null;
}

const BADGE_LABELS = { amber: "Approval needed", green: "Completed", blue: "Action", grey: "Draft" };

export default function HelpPanel() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const content = getContent(location.pathname);

  // Close panel on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (!content) return null;

  return (
    <>
      <button
        className="help-trigger"
        onClick={() => setOpen(o => !o)}
        title="How does this page work?"
        aria-label="Open help"
      >
        ?
      </button>

      {open && (
        <div className="help-overlay" onClick={() => setOpen(false)} />
      )}

      <aside className={`help-panel${open ? " help-panel--open" : ""}`} aria-hidden={!open}>
        <div className="help-panel-header">
          <div>
            <div className="help-phase">{content.phase}</div>
            <div className="help-title">{content.title}</div>
          </div>
          <button className="help-close" onClick={() => setOpen(false)} aria-label="Close help">✕</button>
        </div>

        <div className="help-panel-body">
          <div className="help-steps">
            {content.steps.map((s, i) => (
              <div className="help-step" key={i}>
                <div className="help-step-left">
                  <div className="help-step-num">{s.n}</div>
                  {i < content.steps.length - 1 && <div className="help-step-line" />}
                </div>
                <div className="help-step-body">
                  <div className="help-step-action">{s.action}</div>
                  {s.detail && <div className="help-step-detail">{s.detail}</div>}
                  {s.badge && (
                    <span className={`help-badge help-badge--${s.badge}`}>
                      {BADGE_LABELS[s.badge]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
}
